import type { AnalyticsBrowser } from '@segment/analytics-next';

// Lazy facade around @segment/analytics-next (~90 KB parsed) so it loads as
// an async chunk off the critical path instead of in the initial bundle.
//
// Design goals:
//  - OFF the critical path: the impl chunk is dynamically imported.
//  - IDENTICAL post-boot behavior: once the chunk has loaded, every call is
//    forwarded SYNCHRONOUSLY to AnalyticsBrowser - same microtask, same
//    window.location. This matters because the codebase does
//    `trackEvent(...)` immediately followed by `history.push(...)`; deferring
//    the track past the navigation would attribute it to the wrong page.
//  - Pre-boot calls are queued and replayed in order (AnalyticsBrowser then
//    applies its own pre-ready buffering for delivery, as it does today).
//
// NOTE: AnalyticsBrowser is itself a thenable (resolves to
// [Analytics, Context]), so the import result is wrapped in a plain object to
// stop the import promise from adopting it.

let impl: AnalyticsBrowser | undefined;
// Queued calls made before the impl chunk resolves, replayed in order.
const preInitQueue: Array<(ab: AnalyticsBrowser) => void> = [];

// AnalyticsBrowser is a thenable, so it is wrapped in `{ ab }` everywhere it
// flows through a promise chain - otherwise the chain adopts it and resolves
// to [Analytics, Context] instead of the instance itself.
const loadImpl = (): Promise<{ ab?: AnalyticsBrowser }> =>
  import('./analytics-impl').then(
    (m) => ({ ab: m.analytics }),
    () => ({}), // chunk failed to load (e.g. blocked / stale deploy)
  );

// Resolves to the wrapper (never the bare AnalyticsBrowser) so the chain
// cannot adopt the thenable. Exposed for tests as __analyticsReady.
const ready: Promise<{ ab?: AnalyticsBrowser }> = loadImpl().then(
  (loaded) => {
    if (loaded.ab) {
      flush(loaded.ab);
      return loaded;
    }
    // One retry - a single failed fetch (transient network / stale CDN edge)
    // should not kill analytics for the whole session.
    return loadImpl().then((retry) => {
      if (retry.ab) flush(retry.ab);
      return retry;
    });
  },
);

function flush(ab: AnalyticsBrowser) {
  impl = ab;
  while (preInitQueue.length) {
    preInitQueue.shift()!(ab);
  }
}

// Forward synchronously if the impl is ready (preserves call-time page
// context); otherwise queue until the chunk resolves.
const forward = (fn: (ab: AnalyticsBrowser) => void) => {
  if (impl) {
    fn(impl);
  } else {
    preInitQueue.push(fn);
  }
};

export const analytics = {
  // Matches AnalyticsBrowser: `instance` is undefined until ready
  get instance() {
    return impl?.instance;
  },
  page(...args: Parameters<AnalyticsBrowser['page']>) {
    forward((ab) => ab.page(...args));
  },
  track(...args: Parameters<AnalyticsBrowser['track']>) {
    forward((ab) => ab.track(...args));
  },
  identify(...args: Parameters<AnalyticsBrowser['identify']>) {
    forward((ab) => ab.identify(...args));
  },
};

// Exposed only so a test can await readiness; not part of the public surface.
export const __analyticsReady = ready;
